import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Plus,
  Search as SearchIcon,
  Edit as EditIcon,
  Trash2,
  Mail,
  Phone as PhoneIcon,
  RefreshCw,
  Download,
} from 'lucide-react';
import { validateData, validateSearchQuery, schemas } from '../utils/validation';
import {
  getCustomers,
  addCustomer,
  updateCustomer,
  deleteCustomer,
  recalculateAllCustomerStats,
} from '../firebase/customerService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';

const Customers = ({ currentUser, permissions }) => {
  const isAdmin = permissions?.userType === 'admin';
  const canDelete = isAdmin || permissions?.deleteCustomers === true;
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', status: 'Active' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  // Load data once on mount
  const loadCustomers = useCallback(async () => {
    setLoading(true);
    const result = await getCustomers();
    if (result.success) {
      setCustomers(prevCustomers => {
        // Intelligent merge - protect critical customer fields
        const existingMap = new Map(prevCustomers.map(c => [c.id, c]));
        
        return result.data.map(newCustomer => {
          const existing = existingMap.get(newCustomer.id);
          
          if (!existing) {
            return newCustomer; // New customer, use as-is
          }
          
          // PROTECT IMMUTABLE FIELDS
          return {
            ...newCustomer,
            // Critical fields that should NEVER change randomly
            name: existing.name,
            phone: existing.phone,
            email: existing.email,
            createdAt: existing.createdAt,
          };
        });
      });
    } else {
      showSnackbar(result.error || 'Failed to load customers', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleExportExcel = () => {
    const rows = filteredCustomers;
    let csv = 'Name,Email,Phone,Total Orders,Total Spent (LKR),Status\n';
    rows.forEach(c => {
      const name = `"${(c.name || '').replace(/"/g, '""')}"`;
      const email = `"${(c.email || '').replace(/"/g, '""')}"`;
      const phone = `"${(c.phone || '').replace(/"/g, '""')}"`;
      csv += `${name},${email},${phone},${c.totalOrders || 0},${(c.totalSpent || 0).toFixed(2)},${c.status || ''}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Customers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    
    // First, recalculate all customer stats from actual bills
    const recalcResult = await recalculateAllCustomerStats();
    if (recalcResult.success && recalcResult.updatedCount > 0) {
      console.log(`Recalculated stats for ${recalcResult.updatedCount} customers`);
    }
    
    // Then reload customer data
    await loadCustomers();
    setRefreshing(false);
    showSnackbar('Customers refreshed and stats recalculated');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Validate and sanitize search input
  const handleSearchChange = (e) => {
    const { valid, sanitized } = validateSearchQuery(e.target.value);
    if (valid) {
      setSearchTerm(sanitized);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCount = customers.filter(c => c.status === 'Active').length;

  const handleSave = async () => {
    if (submitting) return; // Prevent double-click

    const validation = validateData(formData, schemas.customer);
    
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      showSnackbar('Please fix validation errors', 'error');
      return;
    }
    
    setValidationErrors([]);
    setSubmitting(true);

    try {
      const sanitizedData = validation.sanitizedData;
      
      if (editingCustomer) {
        // Optimistic update with functional state
        setCustomers(prevCustomers => prevCustomers.map(c => 
          c.id === editingCustomer.id ? { ...c, ...sanitizedData } : c
        ));
        closeModal();
        showSnackbar('Customer updated successfully');
        
        // Log activity
        await logActivity({
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          userName: currentUser?.displayName || currentUser?.email,
          action: ACTIONS.UPDATED_CUSTOMER,
          entityType: ENTITY_TYPES.CUSTOMER,
          entityId: editingCustomer.id,
          entityName: sanitizedData.name,
          details: {
            email: sanitizedData.email,
            status: sanitizedData.status
          }
        });
        
        const result = await updateCustomer(editingCustomer.id, sanitizedData);
        if (!result.success) {
          showSnackbar(result.error || 'Failed to sync update', 'error');
          await loadCustomers();
        }
      } else {
        const result = await addCustomer(sanitizedData);
        if (result.success) {
          setCustomers(prevCustomers => [
            { id: result.id, ...sanitizedData, totalOrders: 0, totalSpent: 0, createdAt: new Date().toISOString() }, 
            ...prevCustomers
          ]);
          closeModal();
          showSnackbar('Customer added successfully');
          
          // Log activity
          await logActivity({
            userId: currentUser?.uid,
            userEmail: currentUser?.email,
            userName: currentUser?.displayName || currentUser?.email,
            action: ACTIONS.CREATED_CUSTOMER,
            entityType: ENTITY_TYPES.CUSTOMER,
            entityId: result.id,
            entityName: sanitizedData.name,
            details: {
              email: sanitizedData.email,
              phone: sanitizedData.phone
            }
          });
        } else {
          showSnackbar(result.error || 'Failed to add customer', 'error');
        }
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      showSnackbar('Failed to save customer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({ name: customer.name, email: customer.email, phone: customer.phone, status: customer.status });
    setValidationErrors([]);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const customerToDelete = customers.find(c => c.id === id);
    setCustomers(prevCustomers => prevCustomers.filter(c => c.id !== id));
    showSnackbar('Customer deleted');
    setDeleteConfirm(null);
    logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.DELETED_CUSTOMER,
      entityType: ENTITY_TYPES.CUSTOMER,
      entityId: id,
      entityName: customerToDelete?.name || 'Unknown',
      details: { email: customerToDelete?.email }
    });
    const result = await deleteCustomer(id);
    if (!result.success) {
      showSnackbar(result.error || 'Failed to sync deletion', 'error');
      await loadCustomers();
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCustomer(null);
    setFormData({ name: '', email: '', phone: '', status: 'Active' });
    setValidationErrors([]);
  };

  const StatCard = ({ value, label }) => (
    <Card>
      <CardContent>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={300} height={50} />
          <Skeleton variant="text" width={400} height={30} />
        </Box>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Skeleton variant="text" width={150} height={40} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={150} height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </Box>
            <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 3, borderRadius: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 1 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Customer Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your customer database and relationships
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard value={customers.length} label="Total Customers" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard value={activeCount} label="Active Customers" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard value={customers.length - activeCount} label="Inactive Customers" />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              All Customers
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Download size={18} />}
                onClick={handleExportExcel}
                size="small"
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Excel</Box>
              </Button>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshCw size={18} />}
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
              </Button>
              <Button 
                variant="contained" 
                startIcon={<Plus size={18} />} 
                onClick={() => setShowModal(true)}
                size="small"
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add New Customer</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Add</Box>
              </Button>
            </Box>
          </Box>

          <TextField
            fullWidth
            placeholder="Search customers..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={20} />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Contact</strong></TableCell>
                  <TableCell align="right"><strong>Total Orders</strong></TableCell>
                  <TableCell align="right"><strong>Total Spent</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {customer.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {customer.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Mail size={14} color="gray" />
                            <Typography variant="caption">{customer.email}</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon size={14} color="gray" />
                          <Typography variant="caption">{customer.phone}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{customer.totalOrders}</TableCell>
                    <TableCell align="right">LKR {customer.totalSpent.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    <TableCell>
                      <Chip
                        label={customer.status}
                        size="small"
                        color={customer.status === 'Active' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEdit(customer)}>
                        <EditIcon size={18} />
                      </IconButton>
                      {canDelete && (
                        <IconButton size="small" onClick={() => setDeleteConfirm(customer)}>
                          <Trash2 size={18} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredCustomers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No customers found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationErrors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth
              label="Email (Optional)"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 254 }}
            />
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 20 }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} disabled={submitting}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submitting ? 'Saving...' : `${editingCustomer ? 'Update' : 'Add'} Customer`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Customer?</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm?.id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Customers;
